import React, { useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { notifyByType } from '../../../ToastMessages';
import DemoHeader from './DemoHeader';
import DemoLinks from './DemoLinks';
import RequestBlock from './RequestBlock';
import NotesCard from './NotesCard';
import ParentDistributor from './ParentDistributor';
import DistributorCard from './DistributorCard';
import ReviewLinksCard from './ReviewLinksCard';
import EditDemoAccount from './EditDemoAccount';
import MoreSettings from './MoreSettings';
import SupportFooter from './SupportFooter';

// Matches the keys MoreSettings' handleBusinessSelected sends onListingReplaced with (see
// MoreSettings.js) minus google_place_id, which MoreSettings tracks itself - EditDemoAccount
// only owns these four fields.
const LISTING_OVERRIDE_FIELDS = ['name', 'address', 'phone', 'url'];

export default function DemoAccountEdit({
    initial,
    endpoints,
    mapsApiKey,
    industries,
    countries,
    supportUrl,
    supportPhone,
    brandedLinkBase,
    googleImageSearchBase,
    limits,
    redirectAlgorithms,
    weightedAlgorithms,
    reviewSites,
    cqrTypes,
    polling,
    timezone,
}) {
    const [demo, setDemo] = useState(initial);
    // Seeded from the initial settings so MoreSettings' mount-time effect
    // (keyed on industryGps.seq) mirrors in the current value instead of
    // clobbering it with an unrelated default on first render.
    const [industryGps, setIndustryGps] = useState(() => ({
        value: Boolean(initial.settings?.allow_gps_feedback),
        seq: 0,
    }));
    // Same pattern as industryGps: MoreSettings' "Replace listing" flow refreshes
    // EditDemoAccount's business-detail fields through a seq bump, never a direct write.
    const [listingOverride, setListingOverride] = useState({ seq: 0, fields: {} });

    // Every edit-island message floats via react-hot-toast (bottom-center) so a
    // save/success/error is visible wherever the user is scrolled - the old
    // in-house Toast rendered as an inline alert pinned to the top of the page.
    // DistributorCard still sends type 'warning' (a distinct icon from plain info),
    // so this stays a lookup map rather than an if/else that would collapse it.
    const handleToast = ({ type, text }) => {
        notifyByType(type, text);
    };

    const handleScorecard = (patch) => {
        setDemo((prev) => ({ ...prev, scorecard: { ...prev.scorecard, ...patch } }));
    };

    const handleNoteAdded = (note) => {
        setDemo((prev) => ({ ...prev, notes: [note, ...(prev.notes || [])] }));
    };

    const handleRequestSent = (request) => {
        setDemo((prev) => ({
            ...prev,
            recent_requests: [request, ...(prev.recent_requests || [])].slice(0, limits.recent_requests ?? 3),
        }));
    };

    const handleDemoPatch = (patch) => {
        setDemo((prev) => ({ ...prev, ...patch }));
    };

    const handleIndustryChange = (industry) => {
        setIndustryGps((prev) => ({
            value: Boolean(industry.allow_gps_feedback_default),
            seq: prev.seq + 1,
        }));
    };

    const handleListingReplaced = (details) => {
        const fields = {};
        LISTING_OVERRIDE_FIELDS.forEach((field) => {
            if (details && Object.prototype.hasOwnProperty.call(details, field)) {
                fields[field] = details[field];
            }
        });
        setListingOverride((prev) => ({ seq: prev.seq + 1, fields }));
    };

    const abilities = demo.abilities || {};

    return (
        <div className="demo-island">
            {/* ~5s (react-hot-toast defaults success to 2s) so a send/save confirmation is not missed. */}
            <Toaster position="bottom-center" toastOptions={{ duration: 5000, success: { duration: 5000 } }} />
            <div className="card mb-3 demo-edit-main-card">
                <div className="card-body">
                    <DemoHeader demo={demo} />
                    <DemoLinks
                        demo={demo}
                        endpoints={endpoints}
                        polling={polling}
                        supportUrl={supportUrl}
                        onScorecard={handleScorecard}
                        onToast={handleToast}
                    />
                    {abilities.send_request && (
                        <RequestBlock
                            demo={demo}
                            endpoints={endpoints}
                            limits={limits}
                            timezone={timezone || 'UTC'}
                            onToast={handleToast}
                            onRequestSent={handleRequestSent}
                        />
                    )}
                    {abilities.add_note && (
                        <NotesCard
                            demo={demo}
                            endpoints={endpoints}
                            limits={limits}
                            timezone={timezone || 'UTC'}
                            onNoteAdded={handleNoteAdded}
                            onToast={handleToast}
                        />
                    )}
                    {abilities.change_distributor ? (
                        <DistributorCard demo={demo} endpoints={endpoints} onDemoChange={setDemo} onToast={handleToast} />
                    ) : (
                        <ParentDistributor demo={demo} supportUrl={supportUrl} />
                    )}
                    <SupportFooter supportUrl={supportUrl} supportPhone={supportPhone} />
                </div>
            </div>
            {abilities.manage_review_links && (
                <ReviewLinksCard
                    demo={demo}
                    endpoints={endpoints}
                    redirectAlgorithms={redirectAlgorithms}
                    weightedAlgorithms={weightedAlgorithms}
                    reviewSites={reviewSites}
                    onDemoPatch={handleDemoPatch}
                    onToast={handleToast}
                />
            )}
            {abilities.update && (
                <EditDemoAccount
                    demo={demo}
                    endpoints={endpoints}
                    industries={industries}
                    brandedLinkBase={brandedLinkBase}
                    googleImageSearchBase={googleImageSearchBase}
                    limits={limits}
                    listingOverride={listingOverride}
                    onDemoChange={setDemo}
                    onIndustryChange={handleIndustryChange}
                    onToast={handleToast}
                />
            )}
            {abilities.update && (
                <MoreSettings
                    demo={demo}
                    endpoints={endpoints}
                    mapsApiKey={mapsApiKey}
                    industries={industries}
                    countries={countries}
                    limits={limits}
                    cqrTypes={cqrTypes}
                    industryGps={industryGps}
                    timezone={timezone || 'UTC'}
                    abilities={abilities}
                    onListingReplaced={handleListingReplaced}
                    onDemoChange={setDemo}
                    onToast={handleToast}
                />
            )}
        </div>
    );
}
